import React from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert
} from '@mui/material';

export interface EbtsField {
    location: string;
    value: string;
}

export interface EbtsRecord {
    type: number;
    idx: number;
    fields: EbtsField[];
}

export interface EbtsImage {
    type: number;
    idx: number;
    position?: string;
    width?: number;
    height?: number;
    png?: string;
    error?: string;
}

export interface EbtsSummary {
    records: EbtsRecord[];
    images: EbtsImage[];
}

const RECORD_TITLES: Record<number, string> = {
    1: 'Type-1 — Transaction Header',
    2: 'Type-2 — Descriptive Data',
    10: 'Type-10 — Facial Image',
    14: 'Type-14 — Fingerprint Image',
};

const recordTitle = (type: number, idx: number, count: number) => {
    const base = RECORD_TITLES[type] || `Type-${type}`;
    return count > 1 ? `${base} (${idx})` : base;
};

/**
 * Renders a parsed EBTS transaction: a field table per record and PNG
 * previews of any embedded imagery (WSQ fingerprints are converted
 * server-side).
 */
const EbtsView: React.FC<{ summary: EbtsSummary }> = ({ summary }) => {
    const typeCounts = summary.records.reduce<Record<number, number>>((acc, r) => {
        acc[r.type] = (acc[r.type] || 0) + 1;
        return acc;
    }, {});

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {summary.records.map(record => {
                const images = summary.images.filter(i => i.type === record.type && i.idx === record.idx);
                return (
                    <Box key={`${record.type}-${record.idx}`}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {recordTitle(record.type, record.idx, typeCounts[record.type])}
                        </Typography>
                        {record.fields.length > 0 && (
                            <TableContainer sx={{ maxHeight: 320, border: '1px solid var(--border-secondary)', borderRadius: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 120 }}>Field</TableCell>
                                            <TableCell>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {record.fields.map((field, i) => (
                                            <TableRow key={`${field.location}-${i}`}>
                                                <TableCell className="mono-id">{field.location}</TableCell>
                                                <TableCell sx={{ wordBreak: 'break-word' }}>{field.value}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {images.length > 0 && (
                            <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                                {images.map((img, i) => (
                                    <Box key={i} textAlign="center">
                                        {img.png ? (
                                            <img
                                                src={`data:image/png;base64,${img.png}`}
                                                alt={img.position || `Type-${img.type} image`}
                                                style={{ maxHeight: 180, maxWidth: 220, border: '1px solid var(--border-primary)', borderRadius: 4 }}
                                            />
                                        ) : (
                                            <Alert severity="warning" sx={{ maxWidth: 220 }}>{img.error || 'Image unavailable'}</Alert>
                                        )}
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            {img.position || `Type-${img.type}`}
                                            {img.width && img.height ? ` · ${img.width}×${img.height}` : ''}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default EbtsView;
